import { isEmpty } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import {
  Card, Grid, CircularProgress, Typography,
  Button as MuiButton,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { useSelector, useInstanceSelector } from '@client/hooks/redux.hooks';
import { getCurrentShow, getProductsForShow } from '@client/selectors/entities/shows';
import { inProgress } from '@client/selectors/helpers/loaders';
import { getSessionUserState } from '@client/selectors/helpers/login';
import { PaddedDivider } from '@client/components/common/PaddedDivider';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { Hide } from '@client/components/common/Hide';
import { useTicketingCheck, TICKET_PURCHASE_FAIL_REASONS } from '@client/hooks/useTicketingCheck';
import { openSignupModal } from '@client/components/Main/Signup';
import { useShareShow } from '@client/components/routes/Shows/Details/Show/TicketCTA/useShareShow.hook';

const useStyles = makeStyles()((theme) => ({
  muiSelectPaper: {
    [theme.breakpoints.down('md')]: {
      maxHeight: `calc(50% - ${theme.spacing(6)}) !important`, // overrides 'calc(100% - 96px)'
    },
  },
  noteText: {
    marginTop: theme.spacing(1 + 1 / 2),
  },
  card: {
    padding: theme.spacing(2),

    '&:not(:last-of-type)': {
      marginBottom: theme.spacing(2),
    },
  },
  signinLink: {
    verticalAlign: 'baseline',
  },
}));

export const TicketLoader = ({ showId }) => {
  const { classes } = useStyles();

  const userState = useSelector(getSessionUserState);
  const isMobile = useIsMobile();

  const { isCancelled, isIncomplete, hasPublicTickets } = useSelector(getCurrentShow);

  const handleShareButtonClick = useShareShow(showId);

  const [badge] = useTicketingCheck({ showId });

  const scope = `fetch-${showId}-SKU`;
  const stripeLoaderInProgress = useInstanceSelector(inProgress, scope);

  const products = useInstanceSelector(getProductsForShow, showId);

  if ((stripeLoaderInProgress !== false || isEmpty(products)) && !isCancelled && !isIncomplete) {
    return (
      <Grid
        alignItems="center"
        container
        justifyContent="flex-start"
        spacing={3}
      >
        <Grid item>
          <CircularProgress />
        </Grid>

        <Grid item>
          <Typography>
            Loading ticket information...
          </Typography>
        </Grid>
      </Grid>
    );
  }

  const SectionComponent = isMobile ? 'div' : Card;
  const sectionProps = isMobile ? {} : { className: classes.card };

  return (
    <>
      {badge && (
        <SectionComponent {...sectionProps}>
          {badge.short === TICKET_PURCHASE_FAIL_REASONS.SOLD_OUT.short && !userState ? (
            <>
              <Typography variant="body2">{badge.long}</Typography>

              <PaddedDivider />

              <Typography variant="body1">
                Sorry! Join the Side Door community so you don't miss out on shows like this coming up!
              </Typography>

              <PaddedDivider />

              <MuiButton
                color="success"
                id="sda-shows-details-ticket-loader-create-account-button"
                variant="contained"
                fullWidth
                onClick={openSignupModal}
              >
                Create Account
              </MuiButton>

              <Hide mdDown>
                <PaddedDivider />

                <Grid
                  container
                  spacing={3}
                >
                  {hasPublicTickets && (
                    <Grid
                      item
                      xs={12}
                    >
                      <MuiButton
                        color="primary"
                        id="sda-shows-details-ticket-loader-share-show-button"
                        onClick={handleShareButtonClick}
                        startIcon={<ShareIcon />}
                        variant="contained"
                      >
                        Share Show
                      </MuiButton>
                    </Grid>
                  )}
                </Grid>
              </Hide>
            </>
          ) : badge.long}
        </SectionComponent>
      )}
    </>
  );
};
